import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SessionSecurityService } from '../../services/session-security.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-session-timeout-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (sessionSecurity.isWarningModalVisible()) {
      <div class="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
        
        <div class="relative w-full max-w-md bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-amber-200/80 dark:border-amber-800/60 overflow-hidden transform transition-all p-6 sm:p-8 flex flex-col items-center text-center">
          
          <!-- Subtle glowing background aura -->
          <div class="absolute -top-24 -left-24 w-48 h-48 bg-amber-500/15 rounded-full blur-3xl pointer-events-none"></div>
          <div class="absolute -bottom-24 -right-24 w-48 h-48 bg-red-500/15 rounded-full blur-3xl pointer-events-none"></div>

          <!-- Alert Header Icon with Pulse Badge -->
          <div class="relative mb-6">
            <div class="w-20 h-20 rounded-2xl bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-800/80 flex items-center justify-center text-amber-600 dark:text-amber-400 shadow-lg shadow-amber-500/10">
              <svg class="w-10 h-10 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            
            <!-- Remaining Seconds Badge -->
            <div class="absolute -bottom-2 -right-2 bg-red-600 text-white font-black text-xs px-2.5 py-1 rounded-full shadow-md border-2 border-white dark:border-slate-900 animate-pulse">
              {{ sessionSecurity.remainingSeconds() }}s
            </div>
          </div>

          <!-- Title & Context -->
          <h3 class="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight mb-2">
            Aviso de Inatividade no Terminal
          </h3>
          
          <p class="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
            Identificamos que seu terminal está inativo há quase 15 minutos. Para garantir a segurança dos prontuários e a conformidade com a 
            <strong class="font-bold text-slate-700 dark:text-slate-200">LGPD</strong>, sua sessão será encerrada em:
          </p>

          <!-- Countdown Big Display -->
          <div class="w-full bg-slate-50 dark:bg-slate-800/80 rounded-2xl py-3 px-4 mb-6 border border-slate-200 dark:border-slate-700/60 flex items-center justify-center gap-3">
            <svg class="w-5 h-5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="text-2xl font-black text-slate-800 dark:text-slate-100 tracking-wider font-mono">
              00:{{ sessionSecurity.remainingSeconds() < 10 ? '0' : '' }}{{ sessionSecurity.remainingSeconds() }}
            </span>
            <span class="text-xs font-semibold text-slate-400 uppercase tracking-widest">restantes</span>
          </div>

          <!-- Action Buttons -->
          <div class="w-full flex flex-col sm:flex-row gap-3">
            <button 
              type="button"
              (click)="sessionSecurity.keepAlive()" 
              class="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-black py-3.5 px-5 rounded-xl shadow-lg shadow-emerald-700/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
              </svg>
              Continuar Sessão
            </button>
            
            <button 
              type="button"
              (click)="sessionSecurity.forceLogout('inactivity')" 
              class="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-bold py-3.5 px-4 rounded-xl active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer">
              <svg class="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
              Encerrar Agora
            </button>
          </div>

        </div>
      </div>
    }
  `
})
export class SessionTimeoutModalComponent {
  sessionSecurity = inject(SessionSecurityService);
}
